import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import theme from '../../theme';
import useNotificationsStream, { channelOf, lastN } from './useNotificationsStream';
import NotificationPanel from './NotificationPanel';

const BellIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3H4a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6" />
    <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
  </svg>
);

export default function NotificationBell({
  context = {},
  title = 'Notifications',
  align = 'right',
  size = 'md',
  onItemClick,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const stream = useNotificationsStream(context, { live: true });
  const { items, unread, loading, load, markRead, markAllRead, toggleFlag, remove, clearAll } = stream;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // ponytail: portal + fixed position, since each bell sits in its own z-indexed
  // stacking context and would otherwise be painted over by sibling bells.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      // Keep the panel inside the viewport on narrow screens (w-80 / sm:w-96).
      const vw = window.innerWidth;
      const width = Math.min(vw >= 640 ? 384 : 320, vw - 16);
      const want = align === 'right' ? r.right - width : r.left;
      setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(want, vw - width - 8)), width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, align]);

  const bellSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const btnPad = size === 'sm' ? 'p-1' : 'p-1.5';

  const handleToggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && unread) markRead(lastN(items, (n) => channelOf(n) === 'master').filter((n) => !n.is_read).map((n) => n.id));
  };

  return (
    <div
      ref={rootRef}
      className="relative inline-flex shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`relative ${btnPad} rounded-full ${theme.btnGhost} text-olive-600`}
        aria-label={`Notifications (${unread} unread)`}
        title={`${title} · notifications`}
      >
        <BellIcon className={bellSize} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
          className={`z-[99999] overflow-hidden pb-2 ${theme.cardBg} ${theme.cardBorder} rounded-2xl shadow-xl`}
        >
          <NotificationPanel
            variant="dropdown"
            defaultChannel="master"
            title={title}
            items={items}
            loading={loading}
            load={load}
            unread={unread}
            filter={filter}
            setFilter={setFilter}
            onMarkAllRead={markAllRead}
            onToggleFlag={toggleFlag}
            onRemove={remove}
            onClearAll={clearAll}
            onItemClick={onItemClick ? (n) => { setOpen(false); onItemClick(n); } : undefined}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
